import Choices from 'choices.js'
import 'choices.js/public/assets/styles/choices.min.css'

class Filters {
  container: Element
  filterSelects: FilterSelect[] = []
  sortSelect: SortSelect | undefined
  sort: Sort | null = null
  filters: Filter[] = []
  productsBoxes: NodeListOf<Element>
  products: Products[] = []
  selectors: Record<Selectors, string>

  constructor(container: Element) {
    this.selectors = SELECTORS
    this.container = container
    this.productsBoxes = this.findProductsBoxes()
    this.productsBoxes.forEach((box) => {
      this.products.push(new Products(box))
    })

    this.initSelects()
    this.listenChange()
    this.listenReset()
  }

  listenChange(): void {
    this.container.addEventListener('bw-filters-changed', () => {
      this.filters = this.getFilters()
      this.applyFilters()

      if (this.sortSelect) {
        this.sort = this.sortSelect.sort
        this.applySort()
      }
    })
  }

  listenReset(): void {
    this.getResetButtons().forEach((btn) => {
      btn.addEventListener('click', () => {
        this.resetFilters()
      })
    })
  }

  resetFilters(): void {
    this.filterSelects.forEach((select) => {
      select.resetValue()
    })

    if (this.sortSelect) {
      this.sortSelect.resetValue()
    }
  }

  getFilters(): Filter[] {
    return this.filterSelects.map((select) => ({
      attrib: select.name,
      wanted: select.value,
    }))
  }

  applyFilters(): void {
    this.products.forEach((products) => {
      products.applyFilters(this.filters)
    })
  }

  applySort(): void {
    this.products.forEach((products) => {
      products.applySort(this.sort)
    })
  }

  initSelects(): void {
    this.findFilterSelects().forEach((select) => {
      this.filterSelects.push(new FilterSelect(select))
    })

    const sortSelect = this.findSortSelect()

    if (sortSelect) {
      this.sortSelect = new SortSelect(sortSelect)
    }
  }

  findFilterSelects(): NodeListOf<Element> {
    return this.container.querySelectorAll(this.selectors.selectFilter)
  }

  findSortSelect(): Element | null {
    return this.container.querySelector(this.selectors.selectSort)
  }

  findProductsBoxes(): NodeListOf<Element> {
    return this.container.querySelectorAll(this.selectors.containerProducts)
  }

  getResetButtons(): NodeListOf<Element> {
    return this.container.querySelectorAll(this.selectors.actionClearFilters)
  }
}

class FilterSelect {
  select: Element
  container: Element | null
  choices: Choices | undefined
  value: string | null = null
  name: string
  selectors: Record<Selectors, string>

  constructor(select: Element) {
    this.selectors = SELECTORS
    this.select = select
    this.container = select.closest(this.selectors.containerFilters)
    this.name = select.getAttribute('name') as string

    this.setUpChoices()

    if (this.choices) {
      this.listenChange()
    }
  }

  resetValue(): void {
    if (this.choices) {
      this.choices.removeActiveItems(0)
      this.choices.setChoiceByValue('')
      this.choices.passedElement.triggerEvent('change')
    }
  }

  listenChange(): void {
    if (this.choices)
      this.choices.passedElement.element.addEventListener('change', () => {
        this.setValue()
        this.emitEvent()
      })
  }

  setValue(): void {
    if (this.choices) {
      const value = this.choices.getValue(true)?.toString()

      if (value) {
        this.value = value
      } else {
        this.value = null
      }
    }
  }

  emitEvent(): void {
    if (this.container) {
      this.container.dispatchEvent(new CustomEvent('bw-filters-changed'))
    }
  }

  setUpChoices(): void {
    /*     const { placeholder } = this.getConfiguration() */

    this.choices = new Choices(this.select, {
      placeholder: true,
      placeholderValue: null,
      searchEnabled: false,
      shouldSort: false,
    })
  }
}

class SortSelect {
  select: Element
  container: Element | null
  choices: Choices | undefined
  sort: Sort | null = null
  name: string
  selectors: Record<Selectors, string>

  constructor(select: Element) {
    this.selectors = SELECTORS
    this.select = select
    this.container = select.closest(this.selectors.containerFilters)
    this.name = select.getAttribute('name') as string

    this.setUpChoices()

    if (this.choices) {
      this.listenChange()
    }
  }

  resetValue(): void {
    if (this.choices) {
      this.choices.removeActiveItems(0)
      this.choices.setChoiceByValue('')
      this.choices.passedElement.triggerEvent('change')
    }
  }

  listenChange(): void {
    if (this.choices)
      this.choices.passedElement.element.addEventListener('change', () => {
        this.setValue()
        this.emitEvent()
      })
  }

  setValue(): void {
    if (this.choices) {
      const value = this.choices.getValue(true)?.toString()

      if (value) {
        const splited = value.split('%#%')

        if (splited.length >= 2) {
          this.sort = {
            attrib: splited[0],
            order: splited[1] === Order.DESC ? Order.DESC : Order.ASC,
          }
        } else {
          this.sort = null
        }
      } else {
        this.sort = null
      }
    }
  }

  emitEvent(): void {
    if (this.container) {
      this.container.dispatchEvent(new CustomEvent('bw-filters-changed'))
    }
  }

  setUpChoices(): void {
    /*     const { placeholder } = this.getConfiguration() */

    this.choices = new Choices(this.select, {
      placeholder: true,
      placeholderValue: null,
      searchEnabled: false,
      shouldSort: false,
    })
  }
}

class Products {
  container: Element

  constructor(container: Element) {
    this.container = container
  }

  applyFilters(filters: Filter[]): void {
    this.container.querySelectorAll('.product').forEach((prod) => {
      if (this.isMatchingAll(filters, prod)) {
        prod.classList.remove('d-none')
      } else {
        prod.classList.add('d-none')
      }
    })

    this.emitUpdateEvent()
  }

  applySort(sort: Sort | null): void {
    const products = this.container.querySelectorAll('.product:not(.d-none)')

    if (sort) {
      let values: number[] = []

      products.forEach((prod) => {
        values.push(Number(prod.getAttribute(`data-${sort.attrib}`)))
      })

      values = values.sort((a: number, b: number) => Number(a) - Number(b))

      if (sort.order === Order.DESC) {
        values = values.reverse()
      }

      products.forEach((prod) => {
        const order =
          values.indexOf(Number(prod.getAttribute(`data-${sort.attrib}`))) -
          values.length
        ;(prod as HTMLElement).style.order = order.toString()
      })
    } else {
      products.forEach((prod) => {
        ;(prod as HTMLElement).style.order = '0'
      })
    }

    this.emitUpdateEvent()
  }

  isMatchingAll(filters: Filter[], product: Element): boolean {
    let isMatching = true

    filters.forEach((filter) => {
      isMatching = isMatching && this.isMatching(filter, product)
    })

    return isMatching
  }

  isMatching(filter: Filter, product: Element): boolean {
    return (
      product.getAttribute(`data-${filter.attrib}`) === filter.wanted ||
      filter.wanted === null
    )
  }

  emitUpdateEvent(): void {
    this.container.dispatchEvent(new CustomEvent('bw-products-filtered'))
  }
}

const SELECTORS: Record<Selectors, string> = {
  containerFilters: '[data-filters-container]',
  selectFilter: '[data-filters-select="filter"]',
  selectSort: '[data-filters-select="sort"]',
  actionClearFilters: '[data-filters-action="clear"]',
  containerProducts: '[data-filters-products]',
}

type Filter = {
  attrib: string
  wanted: string | null
}

type Sort = {
  attrib: string
  order: Order
}

enum Order {
  ASC = 'ASC',
  DESC = 'DESC',
}

type Selectors =
  | 'containerFilters'
  | 'selectFilter'
  | 'selectSort'
  | 'actionClearFilters'
  | 'containerProducts'

window.addEventListener('load', () => {
  document.querySelectorAll(SELECTORS.containerFilters).forEach((elem) => {
    new Filters(elem)
  })
})
